/**
 * テンプレート＋オーバーレイ方式対応デモリセットサービス
 *
 * 新しいデモ環境アーキテクチャ対応:
 * - テンプレートテーブル（読取専用の初期データ）
 * - オーバーレイテーブル（世代ごとの変更履歴）
 * - 世代管理（リセット = 世代番号+1）
 */

import { getSupabaseClient } from '../utils/supabase';
import { AppError, ErrorCategory, ErrorLevel } from '../utils/errorHandler';
import { isDemoMode } from '../utils/environment';

// =============================================================================
// 型定義
// =============================================================================

export interface DemoOverlayResetResult {
  success: boolean;
  message: string;
  generationBefore: number;
  generationAfter: number;
  executionTimeMs: number;
  resetType: 'manual' | 'scheduled' | 'system';
}

export interface DemoOverlayResetOptions {
  onProgress?: (message: string, step: number, totalSteps: number) => void;
  onError?: (error: AppError) => void;
  resetReason?: string;
  triggeredBy?: string;
}

// =============================================================================
// DemoOverlayResetService クラス
// =============================================================================

export class DemoOverlayResetService {
  private static instance: DemoOverlayResetService;
  private currentCompanyId: string | null = null;

  private constructor() {
    // ログ出力を削除
  }

  public static getInstance(): DemoOverlayResetService {
    if (!DemoOverlayResetService.instance) {
      DemoOverlayResetService.instance = new DemoOverlayResetService();
    }
    return DemoOverlayResetService.instance;
  }

  /**
   * 会社IDを設定
   */
  public setCompanyId(companyId: string): void {
    this.currentCompanyId = companyId;
  }

  /**
   * 会社IDを取得
   * 注意: 現在は使用されていませんが、将来的にデモ環境の会社固有処理で必要になる可能性があります
   */
  // @ts-expect-error: 将来的に必要になる可能性があるため保持
  private getCompanyId(): string {
    if (!this.currentCompanyId) {
      throw new AppError(
        '会社IDが設定されていません。ログインし直してください。',
        ErrorCategory.AUTHENTICATION,
        ErrorLevel.HIGH
      );
    }
    return this.currentCompanyId;
  }

  /**
   * デモモードかどうかをチェック
   */
  private validateDemoMode(): void {
    if (!isDemoMode()) {
      throw new AppError(
        'デモリセット機能はデモモードでのみ利用可能です。',
        ErrorCategory.BUSINESS,
        ErrorLevel.HIGH
      );
    }
  }

  /**
   * 現在の世代番号を取得
   */
  public async getCurrentGeneration(): Promise<number> {
    this.validateDemoMode();

    try {
      const supabase = getSupabaseClient();
      if (!supabase) {
        throw new AppError(
          'Supabaseクライアントが初期化されていません',
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH
        );
      }

      const { data, error } = await supabase.rpc('current_demo_generation');

      if (error) {
        throw new AppError(
          `世代番号取得に失敗しました: ${error.message}`,
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH,
          { originalError: error }
        );
      }

      return data || 1;
    } catch (error) {
      // logger.error('Failed to get current generation', { error });
      throw error instanceof AppError
        ? error
        : new AppError(
            `世代番号取得中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
            ErrorCategory.EXTERNAL_API,
            ErrorLevel.HIGH,
            { originalError: error instanceof Error ? error : undefined }
          );
    }
  }

  /**
   * テンプレート＋オーバーレイ方式によるデモデータリセット
   */
  public async resetDemoData(
    options: DemoOverlayResetOptions = {}
  ): Promise<DemoOverlayResetResult> {
    this.validateDemoMode();

    try {
      const startTime = Date.now();
      options.onProgress?.('デモデータリセットを開始しています...', 1, 4);

      // 現在の世代番号を取得
      options.onProgress?.('現在の世代を確認しています...', 2, 4);
      const generationBefore = await this.getCurrentGeneration();

      // Edge Function呼び出し
      options.onProgress?.('リセットを実行しています...', 3, 4);

      const supabase = getSupabaseClient();
      if (!supabase) {
        throw new AppError(
          'Supabaseクライアントが初期化されていません',
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH
        );
      }

      const { data, error } = await supabase.functions.invoke('reset-demo-data', {
        body: {
          reset_reason: options.resetReason || 'デモ体験でのデータ初期化',
          triggered_by: options.triggeredBy || 'user',
        },
      });

      if (error) {
        throw new AppError(
          `デモデータリセットに失敗しました: ${error.message}`,
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH,
          { metadata: { error } }
        );
      }

      if (!data?.success) {
        throw new AppError(
          data?.message || 'デモデータリセットが失敗しました',
          ErrorCategory.EXTERNAL_API,
          ErrorLevel.HIGH,
          { metadata: { response: data } }
        );
      }

      // リセット後の世代番号を確認
      options.onProgress?.('リセット完了を確認しています...', 4, 4);
      const generationAfter = await this.getCurrentGeneration();

      const executionTime = Date.now() - startTime;

      const result: DemoOverlayResetResult = {
        success: true,
        message: data.message || 'デモデータリセットが完了しました',
        generationBefore,
        generationAfter,
        executionTimeMs: executionTime,
        resetType: 'manual',
      };

      return result;
    } catch (_error) {
      const appError =
        _error instanceof AppError
          ? _error
          : new AppError(
              `デモデータリセット中にエラーが発生しました: ${_error instanceof Error ? _error.message : '不明なエラー'}`,
              ErrorCategory.EXTERNAL_API,
              ErrorLevel.HIGH,
              { originalError: _error instanceof Error ? _error : undefined }
            );

      options.onError?.(appError);

      return {
        success: false,
        message: appError.message,
        generationBefore: 0,
        generationAfter: 0,
        executionTimeMs: 0,
        resetType: 'manual',
      };
    }
  }

  /**
   * デモ統計情報を取得
   */
  public async getDemoStats(): Promise<{
    currentGeneration: number;
    lastResetAt: Date | null;
    overlayDataCount: {
      cases: number;
      transporters: number;
    };
  } | null> {
    try {
      this.validateDemoMode();

      const supabase = getSupabaseClient();
      if (!supabase) {
        // logger.warn('Supabaseクライアントが初期化されていません');
        return null;
      }

      const currentGeneration = await this.getCurrentGeneration();

      // 最新リセット情報を取得
      const { data: resetLogs, error: resetError } = await supabase
        .from('demo_reset_logs')
        .select('executed_at')
        .eq('success', true)
        .order('executed_at', { ascending: false })
        .limit(1);

      if (resetError) {
        // logger.warn('Failed to get reset logs', { error: resetError });
      }

      // オーバーレイデータ数を取得
      const [casesResult, transportersResult] = await Promise.all([
        supabase
          .from('company_cases_overlay')
          .select('overlay_id', { count: 'exact' })
          .eq('generation_number', currentGeneration),
        supabase
          .from('company_land_transporters_overlay')
          .select('overlay_id', { count: 'exact' })
          .eq('generation_number', currentGeneration),
      ]);

      return {
        currentGeneration,
        lastResetAt: resetLogs?.[0]?.executed_at ? new Date(resetLogs[0].executed_at) : null,
        overlayDataCount: {
          cases: casesResult.count || 0,
          transporters: transportersResult.count || 0,
        },
      };
    } catch {
      return null;
    }
  }
}

// エクスポート用の便利関数
export const demoOverlayResetService = DemoOverlayResetService.getInstance();

// デフォルトエクスポート
export default DemoOverlayResetService;
